import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/base/Logo";
import { BRAND, TAGLINE, WHATSAPP_DISPLAY, whatsappLink, CONTACT_EMAIL } from "@/constants/site";

interface Column {
  title: string;
  links: { label: string; to?: string; href?: string }[];
}

const COLUMNS: Column[] = [
  {
    title: "Empresa",
    links: [
      { label: "Sobre", to: "/sobre" },
      { label: "Contato", to: "/contato" },
      { label: "Suporte", to: "/suporte" },
      { label: "Área do Cliente", to: "/area-do-cliente" },
    ],
  },
  {
    title: "Hospedagem",
    links: [
      { label: "Hospedagem de Sites", to: "/hospedagem" },
      { label: "Revenda", to: "/revenda" },
      { label: "VPS", to: "/vps" },
      { label: "Servidores", to: "/servidores" },
    ],
  },
  {
    title: "Soluções",
    links: [
      { label: "Soluções Digitais", to: "/solucoes" },
      { label: "Criação de Sites", to: "/sites" },
      { label: "Criação de Logotipo", to: "/sites#logotipo" },
      { label: "Lojas Virtuais", to: "/lojas-virtuais" },
      { label: "Sistemas Web", to: "/sistemas" },
      { label: "Domínios", to: "/dominios" },
    ],
  },
  {
    title: "Conteúdos",
    links: [
      { label: "Blog NXHOST", to: "/blog" },
      { label: "Hospedagem", to: "/blog/hospedagem-de-sites" },
      { label: "Criação de Sites", to: "/blog/criacao-de-sites" },
      { label: "SEO", to: "/blog/seo" },
      { label: "DirectAdmin", to: "/blog/directadmin" },
      { label: "cPanel", to: "/blog/cpanel" },
      { label: "MySQL", to: "/blog/mysql" },
    ],
  },
];

const SOCIALS: { icon: string; label: string; href: string }[] = [
  { icon: "ri-whatsapp-line", label: "WhatsApp", href: whatsappLink() },
  { icon: "ri-instagram-line", label: "Instagram", href: "#" },
  { icon: "ri-facebook-circle-line", label: "Facebook", href: "#" },
];

export default function Footer() {
  return (
    <footer className="bg-foreground-950 text-background-100">
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <Logo tone="light" />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-background-300">{TAGLINE}</p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-background-400">
              Infraestrutura e soluções digitais para empresas que precisam estar online com
              segurança, desempenho e profissionalismo.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  {...(s.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  aria-label={s.label}
                  className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-50/10 text-background-100 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  <i className={`${s.icon} text-lg`} aria-hidden="true"></i>
                </a>
              ))}
            </div>
          </div>

          {COLUMNS.map((col) => (
            <FooterColumn key={col.title} title={col.title}>
              {col.links.map((link) => {
                const className =
                  "text-sm text-background-300 transition-colors hover:text-background-50";
                if (link.to) {
                  return (
                    <li key={link.label}>
                      <Link to={link.to} className={className}>
                        {link.label}
                      </Link>
                    </li>
                  );
                }
                if (link.href) {
                  return (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={className}
                      >
                        {link.label}
                      </a>
                    </li>
                  );
                }
                return null;
              })}
            </FooterColumn>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 border-t border-background-50/10 pt-8 md:grid-cols-3">
          <div className="flex items-start gap-3">
            <i className="ri-whatsapp-line text-xl text-secondary-400" aria-hidden="true"></i>
            <div>
              <p className="text-xs uppercase tracking-wide text-background-400">WhatsApp</p>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-background-50 hover:text-primary-300"
              >
                {WHATSAPP_DISPLAY}
              </a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <i className="ri-mail-line text-xl text-primary-300" aria-hidden="true"></i>
            <div>
              <p className="text-xs uppercase tracking-wide text-background-400">E-mail</p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-sm font-semibold text-background-50 hover:text-primary-300"
              >
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <i className="ri-map-pin-line text-xl text-accent-400" aria-hidden="true"></i>
            <div>
              <p className="text-xs uppercase tracking-wide text-background-400">Atendimento</p>
              <p className="text-sm font-semibold text-background-50">Segunda a sexta, horário comercial</p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-background-50/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 md:px-8 py-5 text-center sm:flex-row sm:text-left">
          <p className="text-xs text-background-400">
            © {new Date().getFullYear()} {BRAND} — Todos os direitos reservados.
          </p>
          <p className="text-xs text-background-500">
            Hospedagem · Servidores · Sites · Lojas Virtuais · Soluções Digitais
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-background-50">{title}</h3>
      <ul className="mt-4 flex flex-col gap-3">{children}</ul>
    </div>
  );
}